import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, PauseCircle, ShieldAlert } from 'lucide-react';
import { cn } from '../lib/utils';

const RISK_CONFIGS = {
  OK: {
    classes: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
    Icon: CheckCircle2,
  },
  CHECK: {
    classes: 'bg-amber-50 text-amber-900 ring-amber-200',
    Icon: AlertTriangle,
  },
  FLAG: {
    classes: 'bg-red-50 text-red-800 ring-red-200',
    Icon: XCircle,
  },
  STAGNANT: {
    classes: 'bg-zinc-200 text-zinc-900 ring-zinc-300',
    Icon: PauseCircle,
  },
};

// ── Single source of truth for alert tier classification ─────────────────────
// Mirrors the backend `get_alert_tier()` in /app/core/agent.py
const HIGH_ALERT_FLAGS  = new Set(['STAGNANT_INVENTORY', 'HIGH_WOS', 'VERY_HIGH_WOS', 'PORTFOLIO_AGEING_BLOCK']);
const HIGH_ALERT_GRADES = new Set(['STAGNANT', 'HIGH_WOS', 'VERY_HIGH']);

export const getAlertTier = (item) => {
  if (!item) return null;
  const rec = item.recommendation;
  if (rec === 'ALLOW') return 'ALLOW';
  const flags = item.flags || [];
  const grade = item.wos_grade;
  const isHigh = flags.some(f => HIGH_ALERT_FLAGS.has(f)) || HIGH_ALERT_GRADES.has(grade);
  if (rec === 'REJECT') return isHigh ? 'HIGH_ALERT' : 'ALERT';
  if (rec === 'REVIEW') return 'ALERT';
  return rec || null;
};

// Visual config keyed by the resolved tier
const TIER_CONFIGS = {
  ALLOW: {
    label: 'ALLOW',
    classes: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
    Icon: CheckCircle2,
  },
  ALERT: {
    label: 'ALERT',
    classes: 'bg-orange-50 text-orange-900 ring-orange-200',
    Icon: AlertTriangle,
  },
  HIGH_ALERT: {
    label: 'HIGH ALERT',
    classes: 'bg-red-50 text-red-800 ring-red-200',
    Icon: ShieldAlert,
  },
};

export function RiskBadge({ grade, label, className }) {
  const config = RISK_CONFIGS[grade] || RISK_CONFIGS['FLAG'];
  const { Icon, classes } = config;
  return (
    <span
      data-testid={`risk-badge-${grade}`}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset whitespace-nowrap',
        classes,
        className
      )}
    >
      <Icon className="w-3 h-3" />
      {grade === 'STAGNANT' ? 'STAGNANT' : label || grade}
    </span>
  );
}

/**
 * Renders the alert tier badge.
 * Pass either `item` (preferred — derives tier) or `tier` directly,
 * or legacy `recommendation` (auto-mapped: REJECT→ALERT, REVIEW→ALERT).
 */
export function RecommendationBadge({ item, tier, recommendation, className }) {
  let resolved = tier;
  if (!resolved && item) resolved = getAlertTier(item);
  if (!resolved && recommendation) {
    resolved = recommendation === 'ALLOW'
      ? 'ALLOW'
      : recommendation === 'REJECT' || recommendation === 'REVIEW'
        ? 'ALERT'
        : null;
  }
  const config = TIER_CONFIGS[resolved] || TIER_CONFIGS.ALERT;
  const { Icon, classes, label } = config;
  return (
    <span
      data-testid={`tier-badge-${resolved || 'unknown'}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ring-inset shadow-sm whitespace-nowrap',
        classes,
        className
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}
